import { useEffect, useState } from 'react';
import type { FieldValues, Path, UseFormReturn } from 'react-hook-form';

import {
  isValidFileType,
  MAX_FILE_SIZE_BYTES,
  MAX_FILES,
  MAX_TOTAL_SIZE_BYTES,
  VALID_FILE_TYPES,
  validateFileSizes,
} from '../tools/fileUploadUtils';

export interface UseFileValidationOptions {
  /** Maximum number of files allowed. @default MAX_FILES (10) */
  maxFiles?: number;
  /** Maximum individual file size in bytes. @default MAX_FILE_SIZE_BYTES (150 MB) */
  maxFileSize?: number;
  /** Maximum total upload size in bytes. @default MAX_TOTAL_SIZE_BYTES (200 MB) */
  maxTotalSize?: number;
  /** Set of allowed MIME types. @default VALID_FILE_TYPES */
  allowedTypes?: string[];
}

/**
 * Hook that provides real-time file validation for react-hook-form file fields.
 *
 * Runs asynchronous MIME-type checks alongside synchronous size/count checks
 * whenever the watched files change. Errors are surfaced both via the form
 * (setError / clearErrors) and a local `validationError` state for easy
 * disabled-button gating.
 *
 * @example
 * ```tsx
 * const files = useWatch({ control: form.control, name: 'files' });
 * const { validationError } = useFileValidation(form, 'files', files);
 * ```
 */
export const useFileValidation = <TFieldValues extends FieldValues>(
  form: UseFormReturn<TFieldValues>,
  fieldName: Path<TFieldValues>,
  files: File[] | undefined,
  options: UseFileValidationOptions = {},
): { validationError: string | null } => {
  const {
    maxFiles = MAX_FILES,
    maxFileSize = MAX_FILE_SIZE_BYTES,
    maxTotalSize = MAX_TOTAL_SIZE_BYTES,
    allowedTypes = VALID_FILE_TYPES,
  } = options;

  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    const validate = async () => {
      form.clearErrors(fieldName);

      if (!files || files.length === 0) {
        setValidationError(null);
        return;
      }

      // Synchronous size / count checks
      const sizeResult = validateFileSizes(files, { maxFiles, maxFileSize, maxTotalSize });
      if (!sizeResult.isValid) {
        setValidationError(sizeResult.error!);
        form.setError(fieldName, { type: 'manual', message: sizeResult.error });
        return;
      }

      // Async MIME type checks
      const typeChecks = await Promise.all(files.map((f) => isValidFileType(f, allowedTypes)));
      const invalidIdx = typeChecks.findIndex((v) => !v);
      if (invalidIdx !== -1) {
        const msg = `File ${files[invalidIdx].name} is not an allowed type.`;
        setValidationError(msg);
        form.setError(fieldName, { type: 'manual', message: msg });
        return;
      }

      setValidationError(null);
    };

    validate();
  }, [files, form, fieldName, maxFiles, maxFileSize, maxTotalSize, allowedTypes]);

  return { validationError };
};
